import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Suspense } from "react";

import { getPartnerDashboard } from "@/lib/partner-portal.functions";
import { SiteHeader } from "@/components/site/SiteHeader";
import { PartnerNav, deliveryLabel } from "@/components/partner/PartnerNav";
import { Skeleton } from "@/components/ui/skeleton";

const dashboardQuery = queryOptions({
  queryKey: ["partner", "dashboard"],
  queryFn: () => getPartnerDashboard(),
});

export const Route = createFileRoute("/_authenticated/partner/")({
  head: () => ({
    meta: [
      { title: "Portal de socios — Mostrando México" },
      { name: "description", content: "Panel de autogestión para socios de distribución: claves de API, webhooks y entregas." },
      { property: "og:title", content: "Portal de socios — Mostrando México" },
      { property: "og:description", content: "Administra tus claves de API y webhooks de la API pública de Mostrando México." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: PartnerHome,
});

function PartnerHome() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-6xl space-y-6 px-4 py-10 sm:px-6 lg:px-8">
        <div>
          <h1 className="font-display text-4xl font-semibold">Portal de socios</h1>
          <p className="mt-1 text-sm text-muted-foreground">Tu integración con la API pública de Mostrando México.</p>
        </div>
        <PartnerNav />
        <Suspense fallback={<Skeleton className="h-72 w-full" />}>
          <Body />
        </Suspense>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
    </div>
  );
}

function Body() {
  const { data } = useSuspenseQuery(dashboardQuery);
  const activeKeys = data.keys.filter((k: any) => k.status === "active").length;
  const activeHooks = data.webhooks.filter((h: any) => h.status === "active").length;

  return (
    <div className="space-y-6">
      <div className="rounded-xl border bg-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-lg font-semibold">{data.me.name}</div>
            <div className="text-sm text-muted-foreground">{data.me.company ?? data.me.email}</div>
          </div>
          <div className="flex gap-2 text-xs">
            <span className="rounded-full border px-3 py-1">Estado: {data.me.status}</span>
            <span className="rounded-full border px-3 py-1">Plan: {data.me.tier}</span>
          </div>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="API keys activas" value={activeKeys} />
        <Stat label="Webhooks activos" value={activeHooks} />
        <Stat label="Entregas fallidas" value={data.failedDeliveries} />
        <Stat
          label="Última actividad"
          value={data.lastActivity ? new Date(data.lastActivity).toLocaleString("es-MX") : "—"}
        />
      </div>

      <div className="rounded-xl border bg-card p-5">
        <h2 className="text-lg font-semibold">Últimas entregas</h2>
        {data.recentDeliveries.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">Aún no hay entregas registradas.</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="py-2">Evento</th>
                  <th className="py-2">Estado</th>
                  <th className="py-2">HTTP</th>
                  <th className="py-2">Intentos</th>
                  <th className="py-2">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {data.recentDeliveries.map((d: any) => (
                  <tr key={d.id} className="border-t">
                    <td className="py-2">{d.event_type}</td>
                    <td className="py-2">{deliveryLabel(d.status, d.next_retry_at)}</td>
                    <td className="py-2">{d.response_status ?? "—"}</td>
                    <td className="py-2">{d.attempts}</td>
                    <td className="py-2">{new Date(d.created_at).toLocaleString("es-MX")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rounded-xl border bg-card p-5">
        <h2 className="text-lg font-semibold">Últimas peticiones API</h2>
        {data.recentRequests.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">Sin peticiones recientes.</p>
        ) : (
          <ul className="mt-3 space-y-1 text-sm">
            {data.recentRequests.map((r: any, i: number) => (
              <li key={i} className="flex flex-wrap gap-2 border-t py-2">
                <span className="font-mono text-xs">{r.method}</span>
                <span className="font-mono text-xs">{r.endpoint}</span>
                <span>{r.status_code}</span>
                <span className="text-muted-foreground">{r.duration_ms} ms</span>
                <span className="text-muted-foreground">{new Date(r.ts).toLocaleString("es-MX")}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
